import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { QarepaBaseSubsDirective } from '@qarepa/common';
import { TicketFileCustom } from '@qaroni-app/ticket/types/ticket-files-custom';
import { TempFilesService } from '@qaroni-core/services/app/temp-files/temp-files.service';
import { TempFiles } from '@qaroni-core/types/files/temp-files';
import { FileValidations } from '@qaroni-core/utils/validations/file-validation';

@Component({
  selector: 'qaroni-upload-files',
  templateUrl: './upload-files.component.html',
  styleUrl: './upload-files.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class UploadFilesComponent
  extends QarepaBaseSubsDirective
  implements OnInit
{
  readonly isRow = input<boolean>(true);
  readonly filesEmitter = output<string[]>();

  private cdr = inject(ChangeDetectorRef);
  private tempFilesService = inject(TempFilesService);

  public filesCustom: TicketFileCustom[] = [];
  private tempFile$ = this.tempFilesService.getUploadFileSubject$();

  public isHovering = false;
  public completedFiles = false;
  public isErrorSize = false;
  public isErrorType = false;
  public iconErrorSize = 'task_alt';
  public iconErrorType = 'task_alt';
  public progressBarMode: 'indeterminate' | 'determinate' = 'indeterminate';

  ngOnInit(): void {
    this.subs.add(this.tempFile$.subscribe(this.getErrorFile));
  }

  get fileStorageIds(): string[] {
    return this.filesCustom
      .map(file => file.uploadedFile?.storageId)
      .filter((id): id is string => id !== undefined);
  }

  public onFileSelected(event: Event): void {
    const files = (event?.target as HTMLInputElement)?.files;
    this.processFileList(files);
  }

  public getFiles(files: FileList): void {
    this.processFileList(files);
  }

  private processFileList(files: FileList): void {
    const fileArray = Array.from(files);
    fileArray.map(this.validateFiles);
  }

  public onUploadedFile(): void {
    this.filesEmitter.emit(this.fileStorageIds);
  }

  public onDeleteFile(index: number): void {
    this.filesCustom.splice(index, 1);
    this.assignIndex();
    this.filesEmitter.emit(this.fileStorageIds);
    this.cdr.markForCheck();
  }

  private assignIndex(): void {
    this.filesCustom.forEach((file, index) => {
      file.index = index;
    });
  }

  private validateFiles = (file: File) => {
    const mb = 3145728;

    const fc: TicketFileCustom = {
      name: file?.name,
      size: file?.size,
      type: file?.type,
      file: file,
      progress: 0,
    };

    if (!FileValidations.validateExtension(file?.name) && file?.size > mb) {
      this.isErrorSize = true;
      this.isErrorType = true;
      this.iconErrorSize = 'unpublished';
      this.iconErrorType = 'unpublished';
      return;
    }

    if (file?.size > mb) {
      this.isErrorSize = true;
      this.isErrorType = false;
      this.iconErrorSize = 'unpublished';
      this.iconErrorType = 'check_circle';
      return;
    } else if (file?.size < mb) {
      if (FileValidations.validateExtension(file?.name)) {
        this.isErrorSize = false;
        this.isErrorType = false;
        this.iconErrorSize = 'check_circle';
        this.iconErrorType = 'check_circle';

        this.filesCustom.push(fc);
        this.assignIndex();
      } else {
        this.isErrorSize = false;
        this.isErrorType = true;
        this.iconErrorSize = 'check_circle';
        this.iconErrorType = 'unpublished';
        return;
      }
    }
  };

  private getErrorFile = (file: TempFiles | null): void => {
    if (!file) {
      this.onDeleteFile(this.filesCustom.length - 1);
    }
  };
}
